import quoteActions from "actions/quote";
import { CloseableContent } from "dyl-components";
import React, { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Dropdown,
    Button,
    Popup,
    Header,
    Ref,
    Portal,
} from "semantic-ui-react";
import ConvertNewCustomerPopup from "shared/ConvertNewCustomerPopup";
import UpsellCustomerPopup from "shared/UpsellCustomerPopup";
import {
    ACTIONS,
    QuoteBuilderContext,
} from "shared/context/QuoteBuilderProvider";
import ConvertAccountToCustomerForm from "shared/forms/ConvertAccountToCustomerForm";

import ConvertContactForm from "shared/forms/ConvertContactForm";
import ConvertToNewCustomerForm from "shared/forms/ConvertToNewCustomerForm";
import ConvertToOpportunityForm from "shared/forms/ConvertToOpportunityForm";
import RestrictedView from "shared/RestrictedView";
import { USER_PERMISSIONS } from "dyl-components";

const ContactConversionControls = ({
    linkedAccountName,
    linkedAccountPrimaryContact,
    account_id,
    contact_id,
    contactName,
    account_stage,
    pipeline_id,
    linkedAccountIsACustomer,
    master_source,
    secondary_source,
    refresh,
    isDeactivatedCustomer,
    location,
    phone,
    email,
    contact,
    onSelectForConversion = () => {}
}) => {
    const modalRef = React.createRef();
    const canConvertToLead = account_stage === "contact";
    const canConvertToOpportunity =
        account_stage === "contact" || account_stage === "lead";
    const canUpsell = account_stage !== "lead";

    const [conversionModalOpen, setConversionModalOpen] = useState("");
    const [isConversionFormModified, setIsConversionFormModified] =
        useState(false);

    const onClose = (e) => {
        const clickedElement = e?.target?.className;
        const isDropdownItemClicked =
            clickedElement === "item" || clickedElement === "text";
        if (!isDropdownItemClicked) {
            setConversionModalOpen("");
            setIsConversionFormModified(false);
        }
    };
    const [position, setPosition] = useState({ x: 0, y: 0, width: 0 });
    const [isOpen, setIsOpen] = useState(false);

    const { onOpenAddProducts, onEditQuote } =
        useContext(QuoteBuilderContext);

    const dispatch = useDispatch();

    const recentOpportunityQuote = useSelector((state) => state.quote.recentOpportunityQuote);
    const recentActiveQuoteId = recentOpportunityQuote?.recent_active_quote_id;
    const recentInactiveQuoteId = recentOpportunityQuote?.recent_inactive_quote_id;
    const isUpsellingFromQuote = !!(canUpsell && (recentActiveQuoteId || recentInactiveQuoteId));

    const onUpsell = () => {
        try {
            if (pipeline_id && canUpsell) {
                if (isUpsellingFromQuote) {
                    return onEditQuote(recentInactiveQuoteId || recentActiveQuoteId, ACTIONS.UPSELL);
                }
                return onOpenAddProducts({ isUpsell: true });
            }
            return onOpenAddProducts({ isUpsell: true });
        } catch (e) {
            console.log(e);
        }
    };

    const getPopupContent = () => {
        switch (conversionModalOpen) {
            case "lead":
                return (
                    <React.Fragment>
                        <Header color="primary">Convert to Lead</Header>
                        <ConvertContactForm
                            size="small"
                            account_id={account_id}
                            contact_id={contact_id}
                            onClose={onClose}
                            refresh={refresh}
                            onModify={(isModified) => {
                                setIsConversionFormModified(isModified);
                            }}
                        />
                    </React.Fragment>
                );
            case "opportunity":
                return (
                    <React.Fragment>
                        <Header color="primary">Convert to Opportunity</Header>
                        <ConvertToOpportunityForm
                            size="small"
                            account_id={account_id}
                            contact_id={contact_id}
                            onClose={onClose}
                            refresh={refresh}
                            onModify={(isModified) => {
                                setIsConversionFormModified(isModified);
                            }}
                            account_stage={account_stage}
                            master_source={master_source}
                            secondary_source={secondary_source}
                        />
                    </React.Fragment>
                );
            case "customer":
                return (
                    <React.Fragment>
                        <Header color="primary">
                            Convert{" "}
                            <Popup
                                trigger={
                                    <span className="Convert__account-name">
                                        {linkedAccountName || contactName}
                                    </span>
                                }
                                content={linkedAccountName || contactName}
                                inverted
                            />{" "}
                            to Customer
                        </Header>
                        <ConvertAccountToCustomerForm 
                            size={"small"}
                            existing_account_id={account_id}
                            owner_id={contact_id}
                            onClose={onClose}
                            refresh={refresh}
                            current_primary_contact_id={linkedAccountPrimaryContact}
                            onModify={(isModified) => {
                                setIsConversionFormModified(isModified);
                            }}
                            pipeline_id={pipeline_id}
                        />
                    </React.Fragment>
                );
            case "new-customer":
                return (
                    <React.Fragment>
                        <Header color="primary">
                            Create Customer-Account
                        </Header>
                        <ConvertToNewCustomerForm 
                            existing_account_id={account_id}
                            onClose={onClose}
                            onModify={(isModified) => {
                                setIsConversionFormModified(isModified);
                            }}
                            owner_id={contact_id}
                            pipeline_id={pipeline_id}
                            refresh={(account_id) => {
                                refresh(account_id);
                            }}
                            size={"small"}
                            account_details={{
                                address: location || {
                                    label: '',
                                    street: '',
                                    additional_street: '',
                                    city: '',
                                    state: '',
                                    zip: '',
                                },
                                email: email?.email,
                                email_type: email?.email_type,
                                phone: phone?.phone,
                                phone_type: phone?.phone_type
                            }}
                        />
                    </React.Fragment>
                );
            default:
                return null;
        }
    };

    const isPrimaryContact = linkedAccountPrimaryContact === contact_id;

    const isUpselling = useSelector((state) => {
        if (pipeline_id) {
            return state.quote.opportunityBeingUpsold === pipeline_id;
        }
        return false;
    });

    const navigate = useNavigate();

    return (
        <React.Fragment>
            <Popup
                content={
                    <CloseableContent onClose={onClose}>
                        {getPopupContent()}
                    </CloseableContent>
                }
                open={conversionModalOpen}
                style={{ zIndex: 5 }}
                position="right center"
                context={modalRef}
                onClose={onClose}
                closeOnDocumentClick={!isConversionFormModified}
            />
            <Ref innerRef={modalRef}>
                <Dropdown
                    loading={isUpselling}
                    as={Button}
                    size="tiny"
                    color="blue"
                    className="ConvertDropdown"
                    text="Convert"
                    onClick={async () => {
                        if (modalRef.current) {
                            await dispatch(quoteActions.clearRecentQuoteByOpportunityId());
                            if (pipeline_id && canUpsell) {
                                await dispatch(
                                    quoteActions.getRecentQuoteByOpportunityId(pipeline_id, {
                                        account_id
                                    })
                                );
                            }
                            const position =
                                modalRef.current.getBoundingClientRect();
                            const scrollY = window.scrollY;
                            setPosition({
                                x: position.left,
                                y: position.top + scrollY + 10,
                                width: position.width,
                            });
                            setIsOpen(!isOpen);
                        }
                    }}
                    onBlur={() => setIsOpen(false)}
                >
                    <Portal open>
                        <Dropdown
                            open={isOpen}
                            style={{
                                position: "absolute",
                                zIndex: 999,
                                left: position.x - 100,
                                top: position.y,
                                visibility: "hidden",
                            }}
                            onClose={() => {
                                setIsOpen(false);
                            }}
                        >
                            <Dropdown.Menu>
                                {canConvertToLead && (
                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.LEAD_EDIT} userRestrict assignmentIds={[(contact?.assignees?.owner_id || 0), (contact?.assignees?.coowner_id || 0)]}>
                                        <Dropdown.Item
                                            style={{ width: 195 }}
                                            onClick={() => {
                                                setConversionModalOpen("lead");
                                                setIsOpen(false);
                                            }}
                                        >
                                            Convert to Lead
                                        </Dropdown.Item>
                                    </RestrictedView>
                                )}
                                {canConvertToOpportunity && (
                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.OPPORTUNITY_EDIT} userRestrict assignmentIds={[(contact?.assignees?.owner_id || 0), (contact?.assignees?.coowner_id || 0)]}>
                                        <Dropdown.Item
                                            style={{ width: 195 }}
                                            onClick={() => {
                                                setConversionModalOpen(
                                                    "opportunity"
                                                );
                                                setIsOpen(false);
                                            }}
                                        >
                                            Convert to Opportunity
                                        </Dropdown.Item>
                                    </RestrictedView>
                                )}
                                {!linkedAccountIsACustomer ? (
                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(contact?.account?.owner_id || 0), (contact?.account?.coowner_id || 0)]}>
                                        <Dropdown.Item 
                                            style={{ width: 195 }} 
                                            onClick={() => {
                                                if (!recentActiveQuoteId) {
                                                    setConversionModalOpen(account_id ? "customer" : "new-customer");
                                                    setIsOpen(false);
                                                }
                                            }}
                                            {...(recentActiveQuoteId ? ({ as: Dropdown, size: "tiny", trigger: "Convert to Customer" }) : {})}
                                            text="Convert to Customer"
                                        >
                                            {recentActiveQuoteId ? (
                                                <Dropdown.Menu direction="right">
                                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(contact?.account?.owner_id || 0), (contact?.account?.coowner_id || 0)]}>
                                                        <Dropdown.Item onClick={() => {
                                                            onSelectForConversion();
                                                            onEditQuote(recentActiveQuoteId);
                                                        }}>
                                                            Use Existing Quote
                                                        </Dropdown.Item>
                                                    </RestrictedView>
                                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(contact?.account?.owner_id || 0), (contact?.account?.coowner_id || 0)]}>
                                                        <Dropdown.Item onClick={() => {setConversionModalOpen(account_id ? "customer" : "new-customer"); onSelectForConversion();}}>
                                                            Don't Use Existing Quote
                                                        </Dropdown.Item>
                                                    </RestrictedView>
                                                </Dropdown.Menu>
                                            ) : "Convert to Customer"}
                                        </Dropdown.Item>
                                    </RestrictedView>
                                ) : (
                                    <>
                                        {isDeactivatedCustomer ? (
                                            <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(contact?.account?.owner_id || 0), (contact?.account?.coowner_id || 0)]}>
                                                <Dropdown.Item
                                                    disabled={!canUpsell}
                                                    style={{ width: 195 }}
                                                    onClick={() => {
                                                        navigate(`/contact/${contact_id}`, { state: { isReactivation: true } })
                                                    }}
                                                >
                                                    Reactivate Customer
                                                </Dropdown.Item>
                                            </RestrictedView>
                                        ) : (
                                            <UpsellCustomerPopup 
                                                open={isOpen && isUpsellingFromQuote}
                                                position={"left center"}
                                                trigger={(
                                                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ORDERS_ADD}>
                                                        <Dropdown.Item
                                                            disabled={!canUpsell}
                                                            style={{ width: 195 }}
                                                            onClick={() => {
                                                                onSelectForConversion();
                                                                onUpsell();
                                                            }}
                                                        >
                                                            Upsell Customer
                                                        </Dropdown.Item>
                                                    </RestrictedView>
                                                )}
                                            />
                                        )}
                                        <ConvertNewCustomerPopup
                                            open={isOpen && isPrimaryContact}
                                            isPrimaryContact={isPrimaryContact}
                                            account_id={account_id}
                                            trigger={
                                                <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(contact?.account?.owner_id || 0), (contact?.account?.coowner_id || 0)]}>
                                                    <Dropdown.Item
                                                        disabled={isPrimaryContact}
                                                        style={{ width: 195 }}
                                                        onClick={() => {
                                                            if (!recentActiveQuoteId) {
                                                                setConversionModalOpen("new-customer");
                                                            }
                                                        }}
                                                        {...(recentActiveQuoteId ? ({ 
                                                            as: Dropdown, 
                                                            size: "tiny", 
                                                            trigger: "Convert New Customer", 
                                                            style: { minWidth: "10em" } 
                                                        }) : {})}
                                                        text="Convert New Customer"
                                                    >
                                                        {recentActiveQuoteId ? (
                                                            <Dropdown.Menu>
                                                                <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(contact?.account?.owner_id || 0), (contact?.account?.coowner_id || 0)]}>
                                                                    <Dropdown.Item onClick={() => {
                                                                        onEditQuote(recentActiveQuoteId, ACTIONS.CONVERT_NEW_CUSTOMER);
                                                                    }}>
                                                                        Use Existing Quote
                                                                    </Dropdown.Item>
                                                                </RestrictedView>
                                                                <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(contact?.account?.owner_id || 0), (contact?.account?.coowner_id || 0)]}>
                                                                    <Dropdown.Item onClick={() => {setConversionModalOpen("new-customer")}}>
                                                                        Don't Use Existing Quote
                                                                    </Dropdown.Item>
                                                                </RestrictedView>
                                                            </Dropdown.Menu>
                                                        ) : "Convert New Customer"}
                                                    </Dropdown.Item>
                                                </RestrictedView>
                                            }
                                        />
                                    </>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Portal>
                </Dropdown>
            </Ref>
        </React.Fragment>
    );
};

export default ContactConversionControls;
